import { FC, useCallback, useEffect, useState } from 'react';

import { ALLOWED_CHARACTERS_REGEX, MAX_PROMO_CODE_LENGTH } from 'utils/constants/promoCode';

import {
    Button,
    Card,
    GridColumn,
    HSpacingContainer,
    Input,
    Skeleton,
    Text,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';
import { ArrowRightOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { activatePromoCode } from 'components/BalanceAndBonuses/PromoCodeCard/activatePromoCode';
import { fetchBalanceAndBonuses } from 'components/common/BalanceAndBonuses/fetchBalanceAndBonuses';

import { useDispatch } from 'store/utils';

const TrlKeys = {
    title: 'BalanceAndBonuses.PromoCodeCard.title',
    placeholder: 'BalanceAndBonuses.PromoCodeCard.placeholder',
};

export const PromoCodeCard: FC<{ loading?: boolean }> = ({ loading }) => {
    const dispatch = useDispatch();
    const trl = useTrls();
    const [value, setValue] = useState('');
    const [isActivating, setIsActivating] = useState(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const codeFromUrl = urlParams.get('code');
        if (codeFromUrl) {
            setValue(codeFromUrl);
        }
    }, []);

    const handleActivate = useCallback(() => {
        const trimmedValue = value.trim();
        if (!trimmedValue || isActivating) {
            return;
        }

        setIsActivating(true);
        dispatch(activatePromoCode(trimmedValue))
            .then(() => {
                setValue('');
                void dispatch(fetchBalanceAndBonuses());
            })
            .finally(() => {
                setIsActivating(false);
            });
    }, [value, dispatch, isActivating]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                handleActivate();
            }
        },
        [handleActivate]
    );

    return (
        <GridColumn xs={4}>
            {loading ? (
                <Skeleton loading height={138} borderRadius={40} />
            ) : (
                <Card style="secondary" padding={24} borderRadius={24} stretched>
                    <VSpacingContainer default={12}>
                        <Text typography="label-2-regular">{trl(TrlKeys.title)}</Text>
                        <HSpacingContainer default={12}>
                            <Input
                                value={value}
                                onChange={(value) => {
                                    if (ALLOWED_CHARACTERS_REGEX.test(value)) {
                                        setValue(value);
                                    }
                                }}
                                onKeyDown={handleKeyDown}
                                placeholder={trl(TrlKeys.placeholder)}
                                size="large"
                                disabled={isActivating}
                                maxLength={MAX_PROMO_CODE_LENGTH}
                            />
                            <Button
                                mode="primary"
                                style="neutral"
                                size="large"
                                icon={<ArrowRightOutlinedSize24 />}
                                hideLabel
                                onClick={handleActivate}
                                loading={isActivating}
                            />
                        </HSpacingContainer>
                    </VSpacingContainer>
                </Card>
            )}
        </GridColumn>
    );
};
